/**
 * client/potions.ts
 * Responsabilidad: Gestión de estado del cliente y llamadas REST para la tienda
 * de pociones y el cinturón de consumibles tácticos.
 */

import { safeGetStorage } from "./utils";
import { questManager } from "./quests";
import {
    POTION_CATALOG,
    POTION_MAX_BELT_SLOTS,
    POTION_DRINK_CAST_MS,
    POTION_GLOBAL_COOLDOWN_MS,
    type PotionId,
    type PotionDefinition,
    type PotionRarity,
    type DailyShopAdType,
} from "../src/shared/constants";

export {
    POTION_CATALOG,
    POTION_MAX_BELT_SLOTS,
    POTION_DRINK_CAST_MS,
    POTION_GLOBAL_COOLDOWN_MS,
    type PotionId,
    type PotionDefinition,
    type PotionRarity,
    type DailyShopAdType,
};

export interface DailyShopAdsStatus {
    potionClaimedToday: boolean;
    crystalsClaimedToday: boolean;
    orbsClaimedToday: boolean;
    msUntilReset: number;
}

export interface PotionStatusResponse {
    potionsInventory: string[];
    equippedPotions?: (string | null)[];
    crystals: number;
    astralOrbs?: number;
    dailyShopAds?: DailyShopAdsStatus;
}

export interface PotionBuyResponse {
    success: boolean;
    crystals?: number;
    potionsInventory?: string[];
    equippedPotions?: (string | null)[];
    quantityPurchased?: number;
    error?: string;
    message?: string;
}

export interface DailyAdRewardResponse {
    success: boolean;
    adType?: DailyShopAdType;
    grantedPotionId?: string;
    potionsInventory?: string[];
    equippedPotions?: (string | null)[];
    crystals?: number;
    astralOrbs?: number;
    dailyShopAds?: DailyShopAdsStatus;
    error?: string;
    message?: string;
}

export class PotionManager {
    private dynamicCatalog: PotionDefinition[] | null = null;
    private currentBelt: (string | null)[] = [null, null, null];
    private currentWarehouse: string[] = [];
    private currentCrystals: number = 0;
    private currentAstralOrbs: number = 0;
    private dailyShopAds: DailyShopAdsStatus | null = null;
    private listeners: Array<(belt: (string | null)[], warehouse: string[], crystals: number, astralOrbs?: number, dailyShopAds?: DailyShopAdsStatus | null) => void> = [];

    constructor() {
        if (typeof window !== "undefined") {
            void this.fetchCatalog();
            window.addEventListener("alchem:auth_change", () => {
                void this.refreshStatus();
            });
        }
    }

    /**
     * Obtiene la lista ordenada de pociones (dinámica de la BD o fallback a POTION_CATALOG).
     */
    public getCatalogList(): PotionDefinition[] {
        return this.dynamicCatalog || Object.values(POTION_CATALOG);
    }

    /**
     * Sincroniza el catálogo autoritativo de pociones desde /api/shop/potions.
     */
    public async fetchCatalog(): Promise<void> {
        try {
            const res = await fetch("/api/shop/potions");
            if (res.ok) {
                const data = await res.json();
                if (data && data.success && Array.isArray(data.catalog)) {
                    this.dynamicCatalog = data.catalog;
                    this.notify();
                }
            }
        } catch (e) {
            console.warn("[Potions] Error al consultar /api/shop/potions:", e);
        }
    }

    public getBelt(): (string | null)[] {
        return [...this.currentBelt];
    }

    public getWarehouse(): string[] {
        return [...this.currentWarehouse];
    }

    public getInventory(): string[] {
        return [...this.currentWarehouse];
    }

    public getCrystals(): number {
        return this.currentCrystals;
    }

    public getAstralOrbs(): number {
        return this.currentAstralOrbs;
    }

    public getDailyShopAds(): DailyShopAdsStatus | null {
        return this.dailyShopAds;
    }

    public subscribe(listener: (belt: (string | null)[], warehouse: string[], crystals: number, astralOrbs?: number, dailyShopAds?: DailyShopAdsStatus | null) => void): () => void {
        this.listeners.push(listener);
        listener(this.currentBelt, this.currentWarehouse, this.currentCrystals, this.currentAstralOrbs, this.dailyShopAds);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    private notify(): void {
        for (const listener of this.listeners) {
            listener(this.currentBelt, this.currentWarehouse, this.currentCrystals, this.currentAstralOrbs, this.dailyShopAds);
        }
    }

    private getAuthToken(): string | null {
        return (
            safeGetStorage("alchem_auth_token") ||
            safeGetStorage("colyseus-auth-token") ||
            null
        );
    }

    public async fetchPotions(): Promise<PotionStatusResponse | null> {
        return this.refreshStatus();
    }

    public async refreshStatus(): Promise<PotionStatusResponse | null> {
        const token = this.getAuthToken();
        if (!token) {
            this.currentBelt = [null, null, null];
            this.currentWarehouse = [];
            this.dailyShopAds = null;
            this.notify();
            return null;
        }

        try {
            const res = await fetch("/api/potions/status", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!res.ok) {
                if (res.status === 401) {
                    this.currentBelt = [null, null, null];
                    this.currentWarehouse = [];
                    this.dailyShopAds = null;
                    this.notify();
                }
                return null;
            }

            const data = (await res.json()) as PotionStatusResponse;
            this.currentWarehouse = Array.isArray(data.potionsInventory)
                ? [...data.potionsInventory]
                : [];
            if (Array.isArray(data.equippedPotions)) {
                this.currentBelt = [
                    data.equippedPotions[0] ?? null,
                    data.equippedPotions[1] ?? null,
                    data.equippedPotions[2] ?? null,
                ];
            } else {
                this.currentBelt = [
                    this.currentWarehouse[0] ?? null,
                    this.currentWarehouse[1] ?? null,
                    this.currentWarehouse[2] ?? null,
                ];
            }
            this.currentCrystals = typeof data.crystals === "number" ? data.crystals : 0;
            this.currentAstralOrbs = typeof data.astralOrbs === "number" ? data.astralOrbs : 0;
            this.dailyShopAds = data.dailyShopAds || null;

            questManager.updateBalances(this.currentCrystals, this.currentAstralOrbs);
            this.notify();
            return data;
        } catch (e) {
            console.warn("[Potions] Error al consultar /api/potions/status:", e);
            return null;
        }
    }

    public async buyPotion(potionId: string, quantity: number = 1): Promise<PotionBuyResponse> {
        const token = this.getAuthToken();
        if (!token) {
            return {
                success: false,
                error: "not_logged_in",
                message: "Debes iniciar sesión con tu cuenta para comprar y guardar pociones.",
            };
        }

        try {
            const res = await fetch("/api/potions/buy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ potionId, quantity }),
            });

            const data = (await res.json()) as PotionBuyResponse;
            if (data.success) {
                if (Array.isArray(data.potionsInventory)) {
                    this.currentWarehouse = data.potionsInventory;
                }
                if (Array.isArray(data.equippedPotions)) {
                    this.currentBelt = [
                        data.equippedPotions[0] ?? null,
                        data.equippedPotions[1] ?? null,
                        data.equippedPotions[2] ?? null,
                    ];
                }
                if (typeof data.crystals === "number") {
                    this.currentCrystals = data.crystals;
                    questManager.updateBalances(this.currentCrystals, this.currentAstralOrbs);
                }
                this.notify();
            }
            return data;
        } catch (e) {
            console.error("[Potions] Error en la petición /api/potions/buy:", e);
            return {
                success: false,
                error: "network_error",
                message: "Error de red al procesar la compra de la poción.",
            };
        }
    }

    /**
     * Equipa una poción en una ranura específica (0, 1 o 2).
     */
    public async equipSlot(slotIndex: number, potionId: string): Promise<boolean> {
        const token = this.getAuthToken();
        if (!token) return false;
        try {
            const res = await fetch("/api/potions/equip", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action: "equip", slotIndex, potionId }),
            });
            if (!res.ok) return false;
            const data = await res.json();
            if (data && data.success) {
                if (Array.isArray(data.equippedPotions)) {
                    this.currentBelt = [
                        data.equippedPotions[0] ?? null,
                        data.equippedPotions[1] ?? null,
                        data.equippedPotions[2] ?? null,
                    ];
                }
                if (Array.isArray(data.potionsInventory)) {
                    this.currentWarehouse = data.potionsInventory;
                }
                this.notify();
                return true;
            }
            return false;
        } catch (e) {
            console.error("[Potions] Error en equipSlot:", e);
            return false;
        }
    }

    /**
     * Desequipa la poción en una ranura específica (0, 1 o 2) regresándola al almacén.
     */
    public async unequipSlot(slotIndex: number): Promise<boolean> {
        const token = this.getAuthToken();
        if (!token) return false;
        try {
            const res = await fetch("/api/potions/equip", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action: "unequip", slotIndex }),
            });
            if (!res.ok) return false;
            const data = await res.json();
            if (data && data.success) {
                if (Array.isArray(data.equippedPotions)) {
                    this.currentBelt = [
                        data.equippedPotions[0] ?? null,
                        data.equippedPotions[1] ?? null,
                        data.equippedPotions[2] ?? null,
                    ];
                }
                if (Array.isArray(data.potionsInventory)) {
                    this.currentWarehouse = data.potionsInventory;
                }
                this.notify();
                return true;
            }
            return false;
        } catch (e) {
            console.error("[Potions] Error en unequipSlot:", e);
            return false;
        }
    }

    /**
     * Reclama una de las 3 ofertas diarias de video en la tienda ("potion" | "crystals" | "orbs").
     */
    public async claimDailyAdReward(adType: DailyShopAdType): Promise<DailyAdRewardResponse> {
        const token = this.getAuthToken();
        if (!token) {
            return {
                success: false,
                error: "not_logged_in",
                message: "Debes iniciar sesión con tu cuenta para reclamar recompensas diarias.",
            };
        }

        try {
            const res = await fetch("/api/shop/daily-ad-reward", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ adType }),
            });

            const data = (await res.json()) as DailyAdRewardResponse;
            if (data.success) {
                if (Array.isArray(data.potionsInventory)) {
                    this.currentWarehouse = data.potionsInventory;
                }
                if (Array.isArray(data.equippedPotions)) {
                    this.currentBelt = [
                        data.equippedPotions[0] ?? null,
                        data.equippedPotions[1] ?? null,
                        data.equippedPotions[2] ?? null,
                    ];
                }
                if (typeof data.crystals === "number") {
                    this.currentCrystals = data.crystals;
                }
                if (typeof data.astralOrbs === "number") {
                    this.currentAstralOrbs = data.astralOrbs;
                }
                if (data.dailyShopAds) {
                    this.dailyShopAds = data.dailyShopAds;
                }
                questManager.updateBalances(this.currentCrystals, this.currentAstralOrbs);
                this.notify();
            }
            return data;
        } catch (e) {
            console.error("[Potions] Error en /api/shop/daily-ad-reward:", e);
            return {
                success: false,
                error: "network_error",
                message: "Error de red al reclamar la recompensa diaria.",
            };
        }
    }
}

export const potionManager = new PotionManager();


